import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import routes from "../../../routes/Routes";
import SearchResultEntity from "../../../../domain/entities/SearchResultEntity";
import StablishmentEntity from "../../../../domain/entities/StablishmentEntity";
import di from "../../../../DependencyInjection";
import SearchStablishmentsUseCase, { SearchStablishmentsUseCaseName } from "../../../../domain/use_cases/stablishment/SearchStablishmentsUseCase";
import DeleteStablishmentUseCase, { DeleteStablishmentUseCaseName } from "../../../../domain/use_cases/stablishment/DeleteStablishmentUseCase";
import CreateStablishmentComponent from "./form/create_stablishment.component";

const useStablishmentListViewModel = () => {
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("search");
    const pageQuery = parseInt(searchParams.get("page") ?? "1");

    const { i18n } = useContext(LocalizationContext);
    const { addToast, openModalDelete, openModalCustom, closeModalCustom, closeModalDelete } = useContext(ModalsContext);
    const { register, handleSubmit, getValues, setValue, watch, formState: { errors } } = useForm();
    const [result, setResult] = useState<SearchResultEntity<StablishmentEntity> | undefined>(undefined);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const navigate = useNavigate();
    let _timerTap: any;

    const _searchStablishments = async () => {
        const data = getValues();
        clearTimeout(_timerTap);
        setResult(undefined);
        const response = await di.get<SearchStablishmentsUseCase>(SearchStablishmentsUseCaseName).call({
            query: data["query"],
            page: data["currentPage"] || 1,
            itemsPerPage: 20,
        });
        if (isLeft(response)) {
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        setResult(response.right);
    };

    const _handleSearch = (data: any) => {
        const search = data["query"];
        const page = data["currentPage"];
        let query = "?";
        if (search && search.length > 0) query += `search=${search}&`;
        if (page) query += `page=${page}&`;
        if (query.endsWith("&")) query = query.slice(0, -1);
        navigate(routes.stablishment_list.relativePath + query);
    };

    const _handleChangeTextSearch = () => {
        clearTimeout(_timerTap);
        const data = getValues();
        _timerTap = setTimeout(() => _handleSearch(data), 500);
    };

    const _handleChange = () => {
        const data = getValues();
        _handleSearch(data);
    };

    const _handleLoad = () => {
        setValue("query", searchQuery);
        setValue("currentPage", pageQuery);
        _searchStablishments();
    };

    const _handleOnChangePage = (page: number) => {
        setValue("currentPage", page);
        const data = getValues();
        _handleSearch(data);
    };

    const _handleOnClickDeleteStablishment = async (stablishment: StablishmentEntity) => {
        const deleteStablishment = async () => {
            const response = await di.get<DeleteStablishmentUseCase>(DeleteStablishmentUseCaseName).call(stablishment.id);
            if (isLeft(response)) return addToast(response.left.code ?? KeyWordLocalization.UnknownError, 'error', undefined);
            closeModalDelete();
            addToast(i18n("Establecimiento eliminado con éxito"), 'success', undefined);
            _searchStablishments();
        };
        openModalDelete(
            i18n("Eliminar Establecimiento"),
            i18n("¿Estás seguro de que deseas eliminar este establecimiento?"),
            deleteStablishment
        );
    };

    const _handleOnClickUpdateStablishment = async (stablishment: StablishmentEntity) => {
        const onUpdated = (updatedStablishment: StablishmentEntity) => {
            closeModalCustom();
            _searchStablishments();
        };
        openModalCustom(
            "lg",
            i18n("Editar Establecimiento"),
            <CreateStablishmentComponent onUpdated={onUpdated} stablishment={stablishment} />
        );
    };

    const _handleOnCreateSingleStablishment = async () => {
        const onCreated = (createdStablishment: StablishmentEntity) => {
            closeModalCustom();
            _searchStablishments();
        };
        openModalCustom(
            "lg",
            i18n("Crear Establecimiento"),
            <CreateStablishmentComponent onCreated={onCreated} />
        );
    };

    useEffect(() => {
        _handleLoad();
    }, [pageQuery, searchQuery]);

    return {
        formInfo: { register, handleSubmit, getValues, setValue, watch, errors },
        result,
        loadingDownload,
        i18n,
        _handleSearch,
        _handleChangeTextSearch,
        _handleChange,
        _handleOnChangePage,
        _handleOnClickDeleteStablishment,
        _handleOnClickUpdateStablishment,
        _handleOnCreateSingleStablishment,
    };
};

export default useStablishmentListViewModel;